"use client";

import { useState } from "react";
import Link from "next/link";
import { Hash } from "lucide-react";

type BlogCategoryItem = {
  name: string;
  slug?: string;
};

type BlogCategoriesProps = {
  categories: Array<string | BlogCategoryItem>;
  // Returns category name string or null for "All"
  onSelect?: (cat: string | null) => void;
  // Optional: receive external active value for controlled usage
  activeCategory?: string | null;
};

export default function BlogCategories({
  categories,
  onSelect,
  activeCategory,
}: BlogCategoriesProps) {
  // Support both controlled (activeCategory prop) and uncontrolled (local state)
  const [localActive, setLocalActive] = useState<string | null>(null);
  const active = activeCategory !== undefined ? activeCategory : localActive;

  const handleSelect = (cat: string | null) => {
    if (activeCategory === undefined) {
      // Uncontrolled — manage locally
      setLocalActive(cat);
    }
    onSelect?.(cat);
  };

  const all = [
    { name: "All", slug: "" },
    ...categories.map((category) =>
      typeof category === "string" ? { name: category } : category,
    ),
  ];

  return (
    <section>
      {/* Section label */}
      <div className="flex items-center gap-3 mb-6">
        <Hash className="text-[#c3003a]" size={20} />
        <span className="text-[#9d86b8] text-xs font-bold uppercase tracking-[0.2em]">
          Browse by Category
        </span>
        <div className="flex-1 h-px bg-white/10" />
        {active && (
          <button
            type="button"
            aria-label="Clear"
            onClick={() => handleSelect(null)}
            className="text-xs text-[#6f5a88] hover:text-[#c3003a] transition-colors font-medium"
          >
            Clear ×
          </button>
        )}
      </div>

      {/* Scrollable pill strip */}
      <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-none snap-x">
        {all.map((cat) => {
          const isAll = cat.name === "All";
          const isActive = isAll ? active === null : active === cat.name;
          const className = `snap-start shrink-0 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 border ${
            isActive
              ? "bg-[#fb397d] border-[#fb397d] text-white shadow-lg shadow-[#fb397d]/30 scale-105"
              : "bg-white/5 border-white/10 text-[#c4b5d9] hover:bg-[#5b32b4]/30 hover:border-[#5b32b4] hover:text-white"
          }`;

          return cat.slug !== undefined ? (
            <Link key={cat.name} href={isAll ? "/blog" : `/blog/category/${cat.slug}`} prefetch={false} className={className}>
              {cat.name}
            </Link>
          ) : (
            <button
              type="button"
              aria-label="Select category"
              key={cat.name}
              onClick={() => handleSelect(isAll ? null : cat.name)}
              className={className}
            >
              {cat.name}
            </button>
          );
        })}
      </div>
    </section>
  );
}
